<template>
  <div class="fa-launcher-root">
    <transition name="fa-widget-expand">
      <section
        v-if="hasOpened"
        v-show="isOpen"
        class="fa-widget-panel"
        :class="{ 'fa-widget-panel--mobile': isMobile }"
        :style="panelStyle"
        role="dialog"
        aria-label="Fleet Assistant"
      >
        <header
          class="fa-widget-header"
          :class="{ 'fa-widget-header--draggable': canMoveResize && !isMaximized }"
          @mousedown.prevent="startDrag"
        >
          <div class="fa-widget-title">
            <i class="fa fa-commenting-o" aria-hidden="true"></i>
            <span>Fleet Assistant</span>
          </div>
          <div class="fa-widget-header-actions">
            <b-button
              v-if="canMoveResize"
              size="sm"
              variant="link"
              class="fa-widget-btn-icon"
              :aria-label="isMaximized ? 'Restore window' : 'Maximize'"
              :title="isMaximized ? 'Restore' : 'Maximize'"
              @mousedown.stop
              @click="toggleMaximize"
            >
              <i :class="isMaximized ? 'fa fa-compress' : 'fa fa-expand'" aria-hidden="true"></i>
            </b-button>
            <b-button
              size="sm"
              variant="link"
              class="fa-widget-close"
              aria-label="Close Fleet Assistant"
              @mousedown.stop
              @click="closePanel"
            >
              <i class="fa fa-times" aria-hidden="true"></i>
            </b-button>
          </div>
        </header>
        <div class="fa-widget-body">
          <fleet_assistant ref="fleetAssistant" embedded @showAlert="forwardAlert" />
        </div>
        <div
          v-if="canMoveResize && !isMaximized"
          class="fa-widget-resize-handle"
          aria-label="Resize"
          @mousedown.prevent="startResize"
        />
      </section>
    </transition>

    <transition name="fa-launcher-pulse">
      <button
        v-show="!isOpen"
        type="button"
        class="fa-widget-toggle"
        :class="{ 'fa-widget-toggle--icon-only': hasUsedLauncher }"
        :aria-label="hasUsedLauncher ? 'Open Fleet Assistant' : 'Ask Fleet Assistant'"
        @click="openPanel"
      >
        <i class="fa fa-commenting-o" aria-hidden="true"></i>
        <span v-if="!hasUsedLauncher">Ask Fleet Assistant</span>
      </button>
    </transition>
  </div>
</template>

<script>
import fleet_assistant from './fleet_assistant.vue'

const MIN_WIDTH = 360
const MIN_HEIGHT = 400
const DEFAULT_WIDTH = 460
const DEFAULT_HEIGHT = 760
const MARGIN = 24
const MOBILE_BREAKPOINT = 768
const STORAGE_KEY = 'faLauncherState'

export default {
  name: 'fleet_assistant_launcher',
  components: {
    fleet_assistant,
  },
  data() {
    return {
      isOpen: false,
      hasOpened: false,
      hasUsedLauncher: false,
      isMaximized: false,
      keydownHandler: null,
      resizeHandler: null,
      isMobile: false,
      panelLeft: null,
      panelTop: null,
      panelWidth: DEFAULT_WIDTH,
      panelHeight: DEFAULT_HEIGHT,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragStartLeft: 0,
      dragStartTop: 0,
      resizing: false,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartWidth: 0,
      resizeStartHeight: 0,
      boundDragMove: null,
      boundDragEnd: null,
      boundResizeMove: null,
      boundResizeEnd: null,
    }
  },
  computed: {
    canMoveResize() {
      return !this.isMobile && this.hasOpened && this.isOpen
    },
    panelStyle() {
      if (this.isMobile) {
        return {}
      }
      if (this.isMaximized) {
        return {
          left: '0',
          top: '0',
          right: '0',
          bottom: '0',
          width: '100%',
          height: '100%',
          borderRadius: '0',
        }
      }
      const left = this.panelLeft != null ? this.panelLeft : (window.innerWidth - MARGIN - this.panelWidth)
      const top = this.panelTop != null ? this.panelTop : (window.innerHeight - MARGIN - this.panelHeight)
      return {
        left: left + 'px',
        top: top + 'px',
        width: this.panelWidth + 'px',
        height: this.panelHeight + 'px',
      }
    },
  },
  mounted() {
    this.keydownHandler = (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.closePanel()
      }
    }
    window.addEventListener('keydown', this.keydownHandler)

    this.updateMobile()
    this.resizeHandler = () => this.updateMobile()
    window.addEventListener('resize', this.resizeHandler)

    this.boundDragMove = (e) => this.onDragMove(e)
    this.boundDragEnd = () => this.endDrag()
    this.boundResizeMove = (e) => this.onResizeMove(e)
    this.boundResizeEnd = () => this.endResize()

    this.loadPanelState()
  },
  beforeDestroy() {
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler)
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
    this.removeDragListeners()
    this.removeResizeListeners()
  },
  methods: {
    loadPanelState() {
      try {
        const raw = window.localStorage && window.localStorage.getItem(STORAGE_KEY)
        if (!raw) return
        const s = JSON.parse(raw)
        if (s.hasUsedLauncher === true) this.hasUsedLauncher = true
        if (!this.isMobile && s.panelLeft != null && typeof s.panelLeft === 'number') this.panelLeft = s.panelLeft
        if (!this.isMobile && s.panelTop != null && typeof s.panelTop === 'number') this.panelTop = s.panelTop
        if (!this.isMobile && s.panelWidth != null && typeof s.panelWidth === 'number') {
          this.panelWidth = Math.max(MIN_WIDTH, Math.min(window.innerWidth - MARGIN, s.panelWidth))
        }
        if (!this.isMobile && s.panelHeight != null && typeof s.panelHeight === 'number') {
          this.panelHeight = Math.max(MIN_HEIGHT, Math.min(window.innerHeight - MARGIN, s.panelHeight))
        }
        if (s.isMaximized === true) this.isMaximized = true
        this.clampPanelPosition()
      } catch (_) {}
    },
    savePanelState() {
      try {
        if (!window.localStorage) return
        const s = {
          hasUsedLauncher: this.hasUsedLauncher,
          panelLeft: this.panelLeft,
          panelTop: this.panelTop,
          panelWidth: this.panelWidth,
          panelHeight: this.panelHeight,
          isMaximized: this.isMaximized,
        }
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
      } catch (_) {}
    },
    updateMobile() {
      this.isMobile = window.innerWidth < MOBILE_BREAKPOINT
      if (this.isMobile) return
      this.clampPanelPosition()
    },
    clampPanelPosition() {
      const maxLeft = window.innerWidth - MIN_WIDTH - MARGIN
      const maxTop = window.innerHeight - MIN_HEIGHT - MARGIN
      if (this.panelLeft != null) {
        this.panelLeft = Math.max(MARGIN, Math.min(maxLeft, this.panelLeft))
      }
      if (this.panelTop != null) {
        this.panelTop = Math.max(MARGIN, Math.min(maxTop, this.panelTop))
      }
    },
    openPanel() {
      this.hasOpened = true
      this.hasUsedLauncher = true
      this.isOpen = true
      if (!this.isMobile) {
        if (this.panelLeft == null || this.panelTop == null) {
          this.panelLeft = window.innerWidth - MARGIN - this.panelWidth
          this.panelTop = window.innerHeight - MARGIN - this.panelHeight
        }
      }
      this.savePanelState()
      this.$nextTick(() => {
        const fa = this.$refs.fleetAssistant
        if (fa && typeof fa.focusInput === 'function') fa.focusInput()
        if (fa && typeof fa.reloadSettings === 'function') fa.reloadSettings()
      })
    },
    closePanel() {
      this.isOpen = false
      this.savePanelState()
    },
    forwardAlert(message, variant, autoclose) {
      this.$emit('showAlert', message, variant, autoclose)
    },
    toggleMaximize() {
      this.isMaximized = !this.isMaximized
      this.savePanelState()
    },
    startDrag(e) {
      if (!this.canMoveResize || this.isMaximized || this.dragging || this.resizing) return
      this.dragging = true
      this.dragStartX = e.clientX
      this.dragStartY = e.clientY
      this.dragStartLeft = this.panelLeft != null ? this.panelLeft : (window.innerWidth - MARGIN - this.panelWidth)
      this.dragStartTop = this.panelTop != null ? this.panelTop : (window.innerHeight - MARGIN - this.panelHeight)
      document.addEventListener('mousemove', this.boundDragMove)
      document.addEventListener('mouseup', this.boundDragEnd)
    },
    onDragMove(e) {
      if (!this.dragging) return
      const dx = e.clientX - this.dragStartX
      const dy = e.clientY - this.dragStartY
      let left = this.dragStartLeft + dx
      let top = this.dragStartTop + dy
      const maxLeft = window.innerWidth - this.panelWidth - MARGIN
      const maxTop = window.innerHeight - this.panelHeight - MARGIN
      left = Math.max(MARGIN, Math.min(maxLeft, left))
      top = Math.max(MARGIN, Math.min(maxTop, top))
      this.panelLeft = left
      this.panelTop = top
    },
    endDrag() {
      this.dragging = false
      this.removeDragListeners()
      this.savePanelState()
    },
    removeDragListeners() {
      document.removeEventListener('mousemove', this.boundDragMove)
      document.removeEventListener('mouseup', this.boundDragEnd)
    },
    startResize(e) {
      if (!this.canMoveResize || this.resizing || this.dragging) return
      e.stopPropagation()
      this.resizing = true
      this.resizeStartX = e.clientX
      this.resizeStartY = e.clientY
      this.resizeStartWidth = this.panelWidth
      this.resizeStartHeight = this.panelHeight
      document.addEventListener('mousemove', this.boundResizeMove)
      document.addEventListener('mouseup', this.boundResizeEnd)
    },
    onResizeMove(e) {
      if (!this.resizing) return
      const dx = e.clientX - this.resizeStartX
      const dy = e.clientY - this.resizeStartY
      let w = this.resizeStartWidth + dx
      let h = this.resizeStartHeight + dy
      w = Math.max(MIN_WIDTH, Math.min(window.innerWidth - MARGIN, w))
      h = Math.max(MIN_HEIGHT, Math.min(window.innerHeight - MARGIN, h))
      this.panelWidth = w
      this.panelHeight = h
      this.clampPanelPosition()
    },
    endResize() {
      this.resizing = false
      this.removeResizeListeners()
      this.savePanelState()
    },
    removeResizeListeners() {
      document.removeEventListener('mousemove', this.boundResizeMove)
      document.removeEventListener('mouseup', this.boundResizeEnd)
    },
  },
}
</script>

<style scoped>
.fa-launcher-root {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 2147483000;
}

.fa-widget-toggle {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2147483001;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #007ac7, #0b94e8);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 92, 153, 0.28);
  cursor: pointer;
  pointer-events: auto;
}

.fa-widget-toggle:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 92, 153, 0.35);
}

.fa-widget-toggle--icon-only {
  padding: 12px;
  min-width: 48px;
  min-height: 48px;
  justify-content: center;
}

.fa-widget-toggle--icon-only .fa {
  margin: 0;
}

.fa-widget-panel {
  position: fixed;
  z-index: 2147483001;
  border: 1px solid #d9dee5;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto;
}

.fa-widget-panel--mobile {
  right: 0;
  bottom: 0;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  max-width: none;
  max-height: none;
  border-radius: 0;
  border: none;
}

.fa-widget-header {
  flex-shrink: 0;
  height: 52px;
  padding: 0 8px 0 16px;
  border-bottom: 1px solid #e3e6ea;
  background: #f7fafc;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.fa-widget-header--draggable {
  cursor: grab;
  user-select: none;
}

.fa-widget-header--draggable:active {
  cursor: grabbing;
}

.fa-widget-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.fa-widget-btn-icon,
.fa-widget-close {
  color: #4c5a67;
  text-decoration: none;
  font-size: 18px;
  line-height: 1;
}

.fa-widget-btn-icon:hover,
.fa-widget-close:hover {
  color: #233746;
}

.fa-widget-title {
  color: #274a63;
  font-size: 15px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.fa-widget-body {
  flex: 1;
  min-height: 0;
}

.fa-widget-resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 20px;
  height: 20px;
  cursor: nwse-resize;
  background: linear-gradient(135deg, transparent 0%, transparent 45%, rgba(0, 0, 0, 0.08) 45%, rgba(0, 0, 0, 0.08) 55%, transparent 55%);
  border-radius: 0 0 14px 0;
}

.fa-widget-resize-handle:hover {
  background: linear-gradient(135deg, transparent 0%, transparent 45%, rgba(0, 0, 0, 0.12) 45%, rgba(0, 0, 0, 0.12) 55%, transparent 55%);
}

.fa-widget-expand-enter-active,
.fa-widget-expand-leave-active {
  transition: opacity 220ms ease, transform 280ms cubic-bezier(0.2, 0.8, 0.2, 1);
  transform-origin: bottom right;
}

.fa-widget-expand-enter,
.fa-widget-expand-leave-to {
  opacity: 0;
  transform: translateY(18px) scale(0.96);
}

.fa-launcher-pulse-enter-active,
.fa-launcher-pulse-leave-active {
  transition: opacity 180ms ease, transform 200ms ease;
}

.fa-launcher-pulse-enter,
.fa-launcher-pulse-leave-to {
  opacity: 0;
  transform: scale(0.94);
}

@media (max-width: 767px) {
  .fa-widget-toggle {
    right: 12px;
    bottom: 12px;
    padding: 10px 14px;
    font-size: 14px;
  }

  .fa-widget-expand-enter-active,
  .fa-widget-expand-leave-active {
    transform-origin: center bottom;
  }
}
</style>
